import type { RecipeVariants } from '@vanilla-extract/recipes'
import clsx from 'clsx'
import { type Ref, useState } from 'react'
import { getInitials } from '#lib/utils/strings'
import './avatar.css'
import { avatarFallbackStyle, avatarRootStyle } from './avatar.css'

interface Props {
  alt?: string
  name?: string | null | undefined
  src?: string | null | undefined
  size?: Pick<
    NonNullable<RecipeVariants<typeof avatarRootStyle>>,
    'size'
  >['size']
  ref?: Ref<HTMLSpanElement>
  className?: string
}

export function Avatar(props: Props) {
  const [error, setError] = useState(false)
  const initials = props.name ? getInitials(props.name) : '?'

  const className = clsx(
    avatarRootStyle({ size: props.size || 'large' }),
    props.className,
  )

  if (
    !props.src ||
    // We currently get URLs like this https://github.com/.png from our GraphQL
    // response for newly forked repos. This leads to 404s and a missing
    // initials fallback. We workaround it by checking explicitly for that
    // avatarUrl and treating it like a null src.
    props.src === 'https://github.com/.png'
  )
    return (
      <span className={className}>
        <span className={avatarFallbackStyle}>{initials}</span>
      </span>
    )

  return (
    <span ref={props.ref} className={className}>
      {error ? (
        <span className={avatarFallbackStyle}>{initials}</span>
      ) : (
        <img
          src={props.src}
          alt={(props.alt ?? props.name) ? `${props.name} avatar` : 'avatar'}
          width={props.size || 32}
          height={props.size || 32}
          loading="eager"
          onError={() => setError(true)}
        />
      )}
    </span>
  )
}
